/**
 * SitePick front — ID 配列ストアの共通実装（favorites / compare が設定して使う）
 *
 * 責務: get / has / add / remove / toggle / clear / sanitize / subscribe。
 * 保存は storage.js（localStorage 安全ラッパ）経由。ID 配列のみ。値の意味は持たない。
 * 変更時に CustomEvent（`eventName`）を発火し、別タブの `storage` イベントも取り込む。
 */

import { readJSON, writeJSON } from "./storage.js";
import { sanitizeIds, sameIds } from "./sanitize.js";

/**
 * @typedef {Object} MutationResult
 * @property {boolean} ok
 * @property {boolean} [active]
 * @property {string} [reason]
 */

/**
 * ID ストアを生成する。
 *
 * @param {Object} config
 * @param {string} config.key        localStorage キー（例 "sitepick_favorites"）
 * @param {string} config.eventName  変更通知イベント名（例 "sitepick:favorites:change"）
 * @param {number} config.max        件数上限
 * @returns {Object}
 */
export function createIdStore({ key, eventName, max }) {
  /** @type {Set<Function>} */
  const subscribers = new Set();

  /** @type {number[]} */
  let ids = sanitizeIds(readJSON(key, []), max);

  function persist() {
    writeJSON(key, ids);
  }

  /**
   * @param {boolean} remote 別タブの変更を取り込んだだけか（このタブの操作ではない）
   */
  function emit(remote) {
    const snapshot = ids.slice();
    const meta = { remote };
    subscribers.forEach((fn) => {
      try {
        fn(snapshot, meta);
      } catch {
        /* subscriber の失敗で全体を止めない */
      }
    });
    try {
      document.dispatchEvent(
        new CustomEvent(eventName, { detail: { ids: snapshot, remote } })
      );
    } catch {
      /* CustomEvent 非対応は無視 */
    }
  }

  /**
   * 内部状態を差し替えて通知（永続化の有無を選べる）。
   *
   * @param {number[]} next
   * @param {boolean} doPersist false = 別タブの storage イベント由来（保存済みなので書き戻さない）
   */
  function set(next, doPersist) {
    const clean = sanitizeIds(next, max);
    if (sameIds(clean, ids)) {
      if (doPersist) {
        persist();
      }
      return false;
    }
    ids = clean;
    if (doPersist) {
      persist();
    }
    emit(!doPersist);
    return true;
  }

  // 別タブでの変更を取り込む。
  window.addEventListener("storage", (event) => {
    if (event.key !== key) {
      return;
    }
    let incoming = [];
    try {
      incoming = event.newValue ? JSON.parse(event.newValue) : [];
    } catch {
      incoming = [];
    }
    set(incoming, false);
  });

  return {
    /** @returns {number[]} 追加順のコピー */
    get() {
      return ids.slice();
    },

    /** @returns {number} */
    count() {
      return ids.length;
    },

    /**
     * @param {number} id
     * @returns {boolean}
     */
    has(id) {
      return ids.indexOf(id) !== -1;
    },

    /** @returns {boolean} 上限に達しているか */
    isFull() {
      return ids.length >= max;
    },

    /** @returns {number} */
    max() {
      return max;
    },

    /**
     * @param {number} id
     * @returns {MutationResult}
     */
    add(id) {
      const [clean] = sanitizeIds([id], 1);
      if (!clean) {
        return { ok: false, reason: "invalid" };
      }
      if (this.has(clean)) {
        return { ok: true, active: true, reason: "exists" };
      }
      if (ids.length >= max) {
        return { ok: false, active: false, reason: "limit" };
      }
      set(ids.concat(clean), true);
      return { ok: true, active: true };
    },

    /**
     * @param {number} id
     * @returns {MutationResult}
     */
    remove(id) {
      if (!this.has(id)) {
        return { ok: true, active: false, reason: "absent" };
      }
      set(
        ids.filter((existing) => existing !== id),
        true
      );
      return { ok: true, active: false };
    },

    /**
     * @param {number} id
     * @returns {MutationResult}
     */
    toggle(id) {
      return this.has(id) ? this.remove(id) : this.add(id);
    },

    /**
     * 保存されていない ID / 存在しなくなった ID を取り除く。
     *
     * @param {number[]} keepIds 残す ID（この配列の順序で並べ替える）
     * @returns {boolean} 変化したか
     */
    retainOnly(keepIds) {
      const keep = sanitizeIds(keepIds, max);
      const next = keep.filter((id) => this.has(id));
      return set(next, true);
    },

    /** localStorage の値を読み直して正規化する。 */
    sanitize() {
      return set(readJSON(key, []), true);
    },

    /** すべて消す。 */
    clear() {
      set([], true);
    },

    /**
     * @param {Function} fn 現在の ID 配列と `{ remote }`（別タブ由来か）を受け取る
     * @returns {Function} 解除関数
     */
    subscribe(fn) {
      subscribers.add(fn);
      return () => subscribers.delete(fn);
    },
  };
}
